import { logger } from '@/lib/logger';
import React, { useCallback, useEffect, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import type { LessonPlayerProps, LessonPlayerRef } from './types';

const SOUNDSLICE_ORIGIN = 'https://www.soundslice.com';
const ZERO_DIM_MAX = 12; // 12 × 750ms ≈ 9s before concluding "no notation"

const LessonPlayerWeb = React.forwardRef<LessonPlayerRef, LessonPlayerProps>(
  (
    {
      lessonId,
      embedUrl,
      startSeconds,
      notationMode,
      videoHeight,
      error,
      setError,
      onReady,
      onTimeUpdate,
      onNotationCheck,
      onNotationVisibilityChange,
    },
    ref
  ) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const hasSeekToStart = useRef(false);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const done = useRef(false);
    const zeros = useRef(0);

    const modeRef = useRef(notationMode);
    useEffect(() => { modeRef.current = notationMode; }, [notationMode]);

    const cbTime = useRef(onTimeUpdate);
    const cbNotation = useRef(onNotationCheck);
    const cbVis = useRef(onNotationVisibilityChange);
    useEffect(() => { cbTime.current = onTimeUpdate; }, [onTimeUpdate]);
    useEffect(() => { cbNotation.current = onNotationCheck; }, [onNotationCheck]);
    useEffect(() => { cbVis.current = onNotationVisibilityChange; }, [onNotationVisibilityChange]);

    const post = useCallback((obj: object) => {
      iframeRef.current?.contentWindow?.postMessage(JSON.stringify(obj), SOUNDSLICE_ORIGIN);
    }, []);

    React.useImperativeHandle(ref, () => ({
      seekTo: (s) => post({ method: 'seek', arg: s }),
      pause: () => post({ method: 'pause' }),
      play: () => post({ method: 'play' }),
      togglePlayPause: () => post({ method: 'togglePlayPause' }),
      toggleFullscreen: () => {
        const f = iframeRef.current as
          | (HTMLIFrameElement & { webkitRequestFullscreen?: () => Promise<void> | void })
          | null;
        if (!f) return;
        // Browsers refuse fullscreen outside a fresh user gesture ("transient
        // activation", "Permissions check failed") and iPadOS Safari only has
        // the webkit-prefixed API. None of that is an app error: ignore quietly.
        try {
          if (document.fullscreenElement) {
            void Promise.resolve(document.exitFullscreen()).catch(() => {});
            return;
          }
          const request = f.requestFullscreen ?? f.webkitRequestFullscreen;
          if (typeof request !== 'function') return;
          void Promise.resolve(request.call(f)).catch((e) => logger.debug('Fullscreen request rejected:', e));
        } catch (e) {
          logger.debug('Fullscreen request failed:', e);
        }
      },
      setPlayerView: (mode) => {
        if (mode === 'video-only') {
          post({ method: 'setNotationVisibility', arg: false });
        } else {
          post({ method: 'setNotationVisibility', arg: true });
          // arg must be an integer: 1 = automatic (side video in landscape, top
          // video in portrait), 2 = left, 3 = top. A string is ignored, leaving
          // any manual drag the user did locked in place.
          post({ method: 'setVideoPosition', arg: 1 });
        }
      },
    }));

    const startPolling = useCallback(() => {
      if (done.current) return;
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => post({ method: 'getNotationDimensions' }), 750);
    }, [post]);

    const stopPolling = () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };

    const applyNotation = (has: boolean) => {
      stopPolling();
      done.current = true;
      cbNotation.current(has);
      if (!has || modeRef.current === 'video-only') {
        post({ method: 'setNotationVisibility', arg: false });
      } else {
        post({ method: 'setNotationVisibility', arg: true });
        post({ method: 'setVideoPosition', arg: 1 });
      }
    };

    const handleIframeLoad = () => {
      try { onReady(); } catch {}
      try { (window as any).__SET_PLAYER_LOADED?.(); } catch {}
      hasSeekToStart.current = false;
      if (startSeconds > 0) {
        hasSeekToStart.current = true;
        setTimeout(() => post({ method: 'seek', arg: startSeconds }), 1000);
      }
      setTimeout(startPolling, 500);
    };

    useEffect(() => {
      const iframe = iframeRef.current;
      if (!iframe) return;

      done.current = false;
      zeros.current = 0;
      stopPolling();

      const win = iframe.contentWindow;
      let dead = false;

      const onMsg = (e: MessageEvent) => {
        if (dead || e.origin !== SOUNDSLICE_ORIGIN || e.source !== win) return;
        try {
          const d = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;

          if (d.method === 'ssReady' || d.method === 'ssPlayerReady') startPolling();

          if (d.method === 'ssNotationLoaded') {
            zeros.current = 0;
            post({ method: 'getNotationDimensions' });
          }

          if (d.method === 'ssCurrentTime') cbTime.current(d.arg);

          if (d.method === 'ssNotationDimensions') {
            const has = d.arg.width > 0 || d.arg.height > 0;
            if (has) {
              applyNotation(true);
            } else {
              zeros.current += 1;
              if (zeros.current >= ZERO_DIM_MAX) applyNotation(false);
            }
          }

          if (d.method === 'ssNotationVisibility') {
            cbVis.current?.(Boolean(d.arg));
          }
        } catch {
          const t = parseFloat(e.data);
          if (!isNaN(t)) cbTime.current(t);
        }
      };

      window.addEventListener('message', onMsg);
      const timePoll = setInterval(() => { if (!dead) post({ method: 'getCurrentTime' }); }, 2000);

      return () => {
        dead = true;
        clearInterval(timePoll);
        stopPolling();
        window.removeEventListener('message', onMsg);
      };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [embedUrl, post, startPolling]);

    return (
      <View
        style={
          videoHeight != null
            ? { height: videoHeight, width: '100%' }
            : StyleSheet.absoluteFillObject
        }
      >
        <iframe
          ref={iframeRef}
          key={`${lessonId}-${embedUrl}`}
          src={embedUrl}
          style={{ width: '100%', height: '100%', border: 'none' }}
          allow="autoplay; fullscreen; encrypted-media; picture-in-picture; screen-wake-lock"
          onLoad={handleIframeLoad}
          onError={() => setError('Failed to load video. Ensure this domain is allow-listed in Soundslice embeds.')}
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-presentation allow-downloads"
          allowFullScreen
        />
        {error && (
          <View style={styles.errorOverlay}>
            <View style={{ padding: 16 }}>
              {/* @ts-ignore */}
              <text style={styles.errorText}>{error}</text>
            </View>
          </View>
        )}
      </View>
    );
  }
);

LessonPlayerWeb.displayName = 'LessonPlayerWeb';
export default React.memo(LessonPlayerWeb);

const styles = StyleSheet.create({
  errorOverlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 3,
    backgroundColor: '#C9C5C3',
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: { color: '#fff', fontSize: 16, textAlign: 'center' },
});
